/**
 * What the Teams lists show about a team delete that runs in the
 * background (TRA-1508), for the owner (Settings > Teams) and for staff
 * (the Employees list and the team's Delete page). The api answers the
 * Delete click with a record; the lists poll the records while one is
 * queued or running, show the step on the team's row, and the failure
 * with a Retry button. Pure functions; the hooks are useTeamDeletions.ts.
 *
 * The page watches the deletes it has seen queued or running, the one its
 * own click answered with included, and compares every fresh read with
 * them (`justFinished`, `justVanished`): a small team's delete can be over
 * before the first read after the click, and that read must still count
 * as the finish.
 */

import type { TeamDeleteRecord } from '@/api/generated/model'

/** How often the lists read the records again while a delete runs. */
export const DELETIONS_POLL_MS = 3_000

/** The step as the row names it, in the order the api runs them. */
const STEP_LABELS: Readonly<Record<string, string>> = {
  conversations: 'conversations',
  jobs: 'Jobs',
  gateway: 'gateway team',
  keys: 'API keys',
  bucket: 'bucket folders',
  database: 'database rows',
  keycloak: 'login group',
}

export type TeamDeleteRowState =
  | { kind: 'idle' }
  | { kind: 'deleting'; label: string }
  | { kind: 'failed'; reason: string; deletionId: string; retryError: string | null }

/** The last Retry's failure, shown on the row of the delete it was for. */
export interface RetryError {
  deletionId: string
  message: string
}

export function isDeleteActive(record: TeamDeleteRecord): boolean {
  return record.status === 'queued' || record.status === 'running'
}

/** Poll while any delete is queued or running; stop once none is. */
export function nextDeletionsPollMs(
  records: readonly TeamDeleteRecord[] | undefined,
): number | false {
  return records !== undefined && records.some(isDeleteActive) ? DELETIONS_POLL_MS : false
}

export function deletingLabel(record: TeamDeleteRecord): string {
  if (record.status === 'queued') return 'Deleting… waiting to start'
  return `Deleting… ${STEP_LABELS[record.step] ?? record.step}`
}

/** What the team's row shows for its latest delete, if any. */
export function teamDeleteState(
  record: TeamDeleteRecord | undefined,
  retryError: RetryError | null,
): TeamDeleteRowState {
  if (record === undefined || record.status === 'done') return { kind: 'idle' }
  if (record.status === 'failed') {
    return {
      kind: 'failed',
      reason: record.error ?? 'The delete failed.',
      deletionId: record.id,
      retryError:
        retryError !== null && retryError.deletionId === record.id ? retryError.message : null,
    }
  }
  return { kind: 'deleting', label: deletingLabel(record) }
}

export function recordsByTeam(
  records: readonly TeamDeleteRecord[],
): ReadonlyMap<string, TeamDeleteRecord> {
  return new Map(records.map((record) => [record.team_id, record]))
}

/** The queued and running deletes of a read, by id: what the page watches next. */
export function activeById(
  records: readonly TeamDeleteRecord[],
): ReadonlyMap<string, TeamDeleteRecord> {
  return new Map(records.filter(isDeleteActive).map((record) => [record.id, record]))
}

/**
 * The watched deletes that are done in this read. The page toasts each
 * one and drops the team from its list.
 */
export function justFinished(
  watched: ReadonlyMap<string, TeamDeleteRecord>,
  current: readonly TeamDeleteRecord[],
): TeamDeleteRecord[] {
  return current.filter((record) => record.status === 'done' && watched.has(record.id))
}

/**
 * The watched deletes that are gone from this read, as last seen. The
 * owner's list holds only the teams the caller owns, and the team's group
 * goes in the delete's last step, so an owner's finished delete leaves
 * the list rather than turning done; but a record also leaves when
 * ownership moved or a token refresh dropped the group, so the page
 * checks the team list before it says the team is deleted
 * (settings/teamDelete.ts `afterTeamVanished`).
 */
export function justVanished(
  watched: ReadonlyMap<string, TeamDeleteRecord>,
  current: readonly TeamDeleteRecord[],
): TeamDeleteRecord[] {
  const now = new Set(current.map((record) => record.id))
  return [...watched.values()].filter((record) => !now.has(record.id))
}

export function deletingToast(teamName: string): string {
  return `Deleting ${teamName} in the background`
}

export function deletedToast(teamName: string): string {
  return `${teamName} deleted`
}

export function deleteFailedText(reason: string): string {
  return `Delete failed: ${reason}`
}
